import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import Form from 'components/form/Form';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import OrderComment from 'pages/Shipments/ActionForms/OrderComment';
import OrderStatuses from 'pages/Shipments/ActionForms/OrderStatuses';
import PackingStatuses from 'pages/Shipments/ActionForms/PackingStatuses';

import { ORDERS, ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

import { CustomerDetails } from './CustomerDetails/CustomerDetails';
import OrderDetails from './OrderDetails/OrderDetails';
import { ShippingDetails } from './ShippingDetails/ShippingDetails';
import useInternationalOrderCheck from './useInternationalOrderCheck';

const EditOrderForm = NiceModal.create(({ id, modalName, adminMode, hasCityError }) => {
	// Hooks and states declaration
	const { t } = useTranslation();
	const { closeModal } = useModalRouter();
	const modal = useModal();
	const axios = useAxiosPrivate();
	const { Currency } = useLocale();
	const { showSuccess, showError } = useAlert();
	const dispatch = useDispatch();
	const isAuthorized = useAccessControl();
	const [isMinimizing, setIsMinimizing] = useState(false);
	const [errorTabs, setErrorTabs] = useState([]);
	const user = useSelector((state) => state.user);

	// Validation schema definition
	const schema = useMemo(
		() =>
			yup.object().shape({
				// Customer Details
				manuallyAdded: yup.boolean().nullable(),
				firstName: yup.string().nullable().required(),
				lastName: yup.string().nullable(),
				email: yup.string().email().nullable(),
				phone: yup.string().nullable().required().phoneNumber(),
				address: yup.object().shape({
					shortAddressCode: yup.string().nullable(),
					text: yup.string().nullable().required(),
					countryName: yup.string().nullable().required(),
					stateName: yup.string().nullable(),
					cityName: yup.string().nullable().required(),
					districtName: yup.string().nullable(),
					neighbourhoodName: yup.string().nullable(),
					lat: yup.number().min(-90).max(90).nullable(),
					lng: yup.number().min(-180).max(180).nullable(),
					zipCode: yup.string().nullable().requiredZipCode(),
					addressInputType: yup.string().nullable().required()
				}),
				// Order Details
				orderID: yup.string().nullable().required(),
				orderDate: yup
					.string()
					.nullable()
					.when('manuallyAdded', ([manuallyAdded], schema) => (manuallyAdded ? schema.required() : schema)),
				orderComments: yup
					.string()
					.nullable()
					.when('products', {
						is: (products) => products?.length > 0,
						then: (schema) => schema,
						otherwise: (schema) => schema.required()
					}),
				products: yup
					.array()
					.when('manuallyAdded', ([manuallyAdded], schema) =>
						manuallyAdded
							? schema.of(
									yup.object().shape({
										productID: yup.number().nullable(),
										name: yup.string().nullable().required(),
										quantity: yup.number().min(1).nullable(),
										price: yup.number().min(0).nullable(),
										tax: yup.number().nullable(),
										total: yup.number().nullable(),
										SKU: yup.string().nullable(),
										hsCode: yup.string().nullable().hsCodeLenght().hsCode()
									})
								)
							: schema.of(
									yup.object().shape({
										productID: yup.number().nullable(),
										quantity: yup.number().nullable(),
										price: yup.number().nullable(),
										tax: yup.number().nullable(),
										total: yup.number().nullable(),
										SKU: yup.string().nullable(),
										name: yup.string().nullable(),
										hsCode: yup.string().nullable().hsCodeLenght().hsCode()
									})
								)
					)
					.when('isInternationalOrder', {
						is: true,
						then: (schema) =>
							schema.test({
								test: (value) => value.length > 0,
								name: 'internationalOrderProductRequired',
								message: 'errorMsg.internationalOrderProductRequired'
							}),
						otherwise: (schema) => schema
					}),
				paymentMethod: yup.string().nullable().required(),
				grandTotal: yup
					.number()
					.nullable()
					.when('manuallyAdded', ([manuallyAdded], schema) =>
						manuallyAdded ? schema.min(1).required() : schema
					),
				currency: yup.string().nullable().required().customOrderCurrencyValidation(),
				totalDue: yup
					.number()
					.nullable()
					.when(['manuallyAdded', 'paymentMethod'], ([manuallyAdded, paymentMethod], schema) =>
						paymentMethod?.toLowerCase() === 'cod' && manuallyAdded ? schema.min(1).required() : schema
					),
				weight: yup.number().min(0.01).nullable().required(),
				packageCount: yup.number().min(1).nullable().required(),
				refNo: yup.string().nullable(),
				warehouseID: yup.number().nullable(),
				branchID: yup.number().nullable(),
				brandID: yup
					.number()
					.nullable()
					.when([], {
						is: () => user.userType === 'BrandManager',
						then: (schema) => schema.required()
					}),
				// Shipping Details
				deliverySlotDate: yup
					.object()
					.shape({
						date: yup.date().nullable(),
						from: yup.string().nullable(),
						to: yup.string().nullable()
					})
					.nullable(),
				shippingDescription: yup.string().nullable(),
				boxes: yup.array().of(
					yup.object().shape({
						ID: yup.number().nullable(),
						width: yup.number().positive().nullable().required(),
						height: yup.number().positive().nullable().required(),
						length: yup.number().positive().nullable().required(),
						weight: yup.number().positive().nullable().required()
					})
				)
			}),
		[t]
	);

	// Form setup using react-hook-form
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: async () => await fillForm()
	});

	const {
		handleSubmit,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading },
		setValue,
		setError
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful && !isMinimizing);

	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	};

	// Check if order id is provided
	useLayoutEffect(() => {
		if (!id) {
			showError({
				header: 'error',
				message: 'alert.orderNotSelected'
			});
			handleCloseModal();
		}
	}, [id]);

	const tabs = useMemo(
		() => [
			{
				label: 'customerDetails',
				component: () => <CustomerDetails isAdmin={adminMode} formMode="edit" />,
				fields: ['firstName', 'lastName', 'email', 'phone', 'address'],
				error: errorTabs.includes('customerDetails')
			},
			{
				label: 'orderDetails',
				component: () => <OrderDetails isAdmin={adminMode} otoOrderID={id} formMode="edit" />,
				fields: [
					'orderNumber',
					'orderDate',
					'paymentMethod',
					'grandTotal',
					'currency',
					'totalDue',
					'packageCount',
					'weight',
					'products',
					'orderComments',
					'warehouseID',
					'branchID',
					'brandID',
					'refNo',
					'boxes'
				],
				error: errorTabs.includes('orderDetails')
			},
			{
				label: 'shippingDetails',
				component: () => <ShippingDetails isAdmin={adminMode} formMode="edit" />,
				fields: [
					'deliveryCompany',
					'shipmentID',
					'deliverySlotDate',
					'shippingDescription',
					'drivingDistance',
					'trackingURL',
					'customValue',
					'customCurrency',
					'cancelComment',
					'cancelReason',
					'onHoldComment',
					'onHoldReason',
					'returnReason'
				],
				error: errorTabs.includes('shippingDetails')
			},
			...(isAuthorized(['WMSModule', 'WMSAdvanceModule'])
				? [
						{
							label: 'packingStatusHistory',
							component: () => <PackingStatuses ID={id} />,
							fields: ['packingStatusHistory'],
							error: errorTabs.includes('packingStatusHistory')
						}
					]
				: []),
			{
				label: 'statusHistory',
				component: () => <OrderStatuses formMode="edit" ID={id} />
			},
			{
				label: 'EditOrderForm.comments',
				component: () => <OrderComment formMode="edit" ID={id} />
			}
		],
		[errorTabs]
	);

	const onError = (errors) => {
		AppLogger.log(errors);
		const errorTabs = [];

		tabs.forEach((tab) => {
			if (tab.fields?.some((field) => errors[field])) {
				errorTabs.push(tab.label);
			}
		});
		if (errorTabs.length > 0) {
			showError({
				header: t('error.validationError'),
				message: t('error.tabsHaveErrors', {
					tabNames: errorTabs.map((tab) => t(tab)).join(', '),
					count: errorTabs.length
				})
			});
		}
		setErrorTabs(errorTabs);
	};

	const { isInternationalOrder } = useInternationalOrderCheck({
		orderCountryCode: watch('address.countryName'),
		pickupLocationCountryCode: watch('originCountry')
	});

	useEffect(() => {
		setValue('isInternationalOrder', isInternationalOrder);
	}, [isInternationalOrder, setValue]);

	const fillForm = useCallback(
		async () =>
			await axios
				.post(ORDERS.GET_ORDER_BY_ID, { ID: Number(id) })
				.then((response) => {
					const { data, status } = response;

					if (data.code) {
						AppLogger.error(`EditOrderForm:fillForm: ${data.code} ${data.message} ${data.description}`);
						showError({
							header: 'error',
							message: data.code
						});
						handleCloseModal();
					}
					if (status === 200) {
						let correctedWeight =
							Array.isArray(data.boxes) && data.boxes.length > 0
								? data.boxes.reduce((acc, box) => acc + box.weight, 0)
								: isNaN(data.orderDetails.weight)
									? 1
									: data.orderDetails.weight;

						correctedWeight = Number(correctedWeight.toFixed(2));

						return {
							// Customer Details
							firstName: data.customerDetails.firstName ?? null,
							lastName: data.customerDetails.lastName ?? null,
							warehouseID: data.customerDetails.warehouseID ?? null,
							branchID: data.customerDetails.branchID ?? null,
							email: data.customerDetails.email ?? null,
							phone: data.customerDetails.phone ?? null,
							address: {
								text: data.customerDetails.address.text ?? null,
								countryName: data.customerDetails.address.countryName ?? null,
								stateName: data.customerDetails.address.stateName ?? null,
								cityName: data.customerDetails.address.cityName ?? null,
								districtName: data.customerDetails.address.districtName ?? null,
								neighbourhoodName: data.customerDetails.address.neighbourhoodName ?? null,
								lat: data.customerDetails.address.lat ?? null,
								lng: data.customerDetails.address.lng ?? null,
								zipCode: data.customerDetails.address.zipCode ?? null,
								addressInputType: data.customerDetails.address.addressInputType ?? 'fullTextAddress',
								countryID: null,
								cityID: null,
								...data.customerDetails.address
							},
							// Order Details
							orderID: data.orderDetails.orderID ?? null,
							orderDate: data.orderDetails.orderDate ?? null,
							orderComments: data.orderDetails.orderComments ?? null,
							brandID: data.customerDetails.brandID ?? null,
							paymentMethod: data.orderDetails.paymentMethod ?? 'paid',
							currency: data.orderDetails.currency ?? Currency,
							grandTotal: data.orderDetails.grandTotal ?? null,
							totalDue: data.orderDetails.totalDue ?? null,
							packageCount: data.orderDetails.packageCount ?? 1,
							refNo: data.orderDetails.refNo ?? null,
							weight: correctedWeight,
							waveID: data.orderDetails.waveID ?? null,

							// Shipping Details
							deliveryCompany: data.shipmentDetails.deliveryCompany ?? null,
							shipmentID: data.shipmentDetails.shipmentID ?? null,
							deliverySlotDate: data.shipmentDetails.deliverySlotDate ?? null,
							shippingDescription: data.shipmentDetails.shippingDescription ?? null,
							drivingDistance: data.shipmentDetails.drivingDistance ?? null,
							trackingURL: data.shipmentDetails.trackingURL ?? null,
							customValue: data.shipmentDetails.customValue ?? null,
							customCurrency: data.shipmentDetails.customCurrency ?? Currency,

							cancelComment: data.shipmentDetails.cancelComment ?? null,
							cancelReason: data.shipmentDetails.cancelReason ?? null,
							onHoldComment: data.shipmentDetails.onHoldComment ?? null,
							onHoldReason: data.shipmentDetails.onHoldReason ?? null,
							returnReason: data.shipmentDetails.returnReason ?? null,
							orderDocs: data.orderDocs ?? null,
							// Status History
							status: data.shipmentDetails.status ?? null,
							// Item details
							products: data.itemsDetails,
							boxes: data.boxes ?? null,
							originCountry: data.shipmentDetails.originCountry ?? null,
							manuallyAdded: data.orderDetails.manuallyAdded ?? true,
							isInternationalOrder: !!data.shipmentDetails.isInternationalShipment
						};
					} else {
						AppLogger.error(response);
						handleCloseModal();
						showError();
					}
				})
				.catch((error) => {
					AppLogger.error(error);
					showError();
					handleCloseModal();
				}),
		[]
	);

	const onSubmit = async (data) => {
		const newData = { ...data };

		const updateOrderData = {
			// Customer Details
			otoOrderID: Number(id),
			firstName: newData.firstName,
			lastName: newData.lastName,
			warehouseID: newData.warehouseID,
			branchID: newData.branchID,
			email: newData.email,
			phone: newData.phone,
			address: {
				...newData.address,
				countryID: null,
				cityID: null
			},
			// Order Details
			orderDate: newData.orderDate,
			orderComments: newData.orderComments,
			brandID: newData.brandID,
			paymentMethod: newData.paymentMethod,
			grandTotal: Number(newData.grandTotal),
			totalDue: newData.paymentMethod?.toLowerCase() === 'cod' ? Number(newData.totalDue) : null,
			weight: Number(newData.weight),
			packageCount: Number(newData.packageCount),
			refNo: newData.refNo,
			currency: newData.currency,
			// shipment details
			deliverySlotDate: {
				date: newData.deliverySlotDate?.date ?? null,
				from: newData.deliverySlotDate?.from ?? null,
				to: newData.deliverySlotDate?.to ?? null
			},
			shippingDescription: newData.shippingDescription,
			products: newData.products,
			boxes: newData.boxes
		};

		if (newData.isInternationalOrder && adminMode) {
			updateOrderData.customCurrency = newData.customCurrency;
			updateOrderData.customValue = newData.customValue;
		}

		await axios
			.post(ORDER_ACTIONS.UPDATE_ORDER, updateOrderData)
			.then((response) => {
				const { status } = response;
				if (status === 200) {
					dispatch(updateDynamicListsInPage());
					showSuccess();
					modal.resolve({
						success: true
					});
					handleCloseModal();
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				if (error.response?.data.code) {
					showError({
						message: error.response?.data.code
					});
				} else {
					AppLogger.error(error);
					showError();
				}
			});
	};

	useEffect(() => {
		if (hasCityError && !isFormLoading) {
			setError('address.cityName', {
				type: 'manual',
				message: 'price-calculator.city_not_found.title'
			});
		}
	}, [hasCityError, isFormLoading]);

	return (
		<Modal
			fullwidth
			maxWidth="xx-large"
			name={modalName}
			className="modal-editOrderForm"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label="editOrder"
					tooltip={watch('orderID')}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">
					{t('editOrder')} (<strong>{watch('orderID')}</strong>)
				</Typography>
			</ModalTitle>

			<ModalContent style={{ width: '100%' }}>
				<Form borderless formMethods={formMethods}>
					<Tabs data={tabs} />
				</Form>
			</ModalContent>

			<ModalActions>
				<Button
					label="cancel"
					variant="blackOutline"
					onClick={() => {
						handleCloseModal();
					}}
					css={css`
						text-decoration: underline;
						border: none !important;
					`}
				/>

				<Button
					icon={faFloppyDisk}
					label="save"
					onClick={handleSubmit(onSubmit, onError)}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					variant="blue"
					loading={isLoading}
				/>
			</ModalActions>
		</Modal>
	);
});

export default EditOrderForm;
